import {
  SurveyEndpointRecord,
  useSiteList,
  useSurveyEndpoints,
} from "@inrange/building-manager-api-client";
import { getApiHostname } from "@inrange/building-manager-api-client/config";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { Button, Card, Table } from "react-bootstrap";
import getCustomerAppDomain from "../../utils/getCustomerAppDomain";
import SurveyEndpointLanguageSelector from "../../components/SurveyEndpointLanguageSelector";
import { SurveyProjectRow } from "../../components/SurveyProjectRow";

interface SiteOption {
  id: string;
  name: string;
  isAssociated: boolean;
}

const selectStyles = {
  dropdownIndicator: (base: Record<string, unknown>) => ({ ...base, padding: 4 }),
  clearIndicator: (base: Record<string, unknown>) => ({ ...base, padding: 4 }),
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
};

const ProjectsList = ({
  endpoint,
  siteOptions,
  sitesLoading,
  surveyUrl,
  apiHostname,
}: {
  endpoint: SurveyEndpointRecord;
  siteOptions: SiteOption[];
  sitesLoading: boolean;
  surveyUrl: string;
  apiHostname: string;
}) => {
  const projects = endpoint.projects || [];
  if (projects.length === 0) {
    return (
      <div className="text-muted small p-2">
        No projects for this endpoint.
      </div>
    );
  }
  return (
    <Table size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Project</th>
          <th>Survey</th>
          <th>Cost sheet</th>
          <th>Site</th>
          <th>Last updated</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {projects.map((p) => (
          <SurveyProjectRow
            key={p.id}
            endpointId={endpoint.id}
            project={p}
            siteOptions={siteOptions}
            sitesLoading={sitesLoading}
            apiHostname={apiHostname}
            projectNameHref={surveyUrl}
            selectInputIdPrefix="org-site-selector-"
            selectMinWidth={320}
            selectStyles={selectStyles}
          />
        ))}
      </tbody>
    </Table>
  );
};

const OrgSurveys = ({ organisation }: { organisation: Organisation }) => {
  const { fetchOrgSurveys } = useSurveyEndpoints(organisation.id);
  const { siteList, siteListQuery } = useSiteList();

  const endpoints = fetchOrgSurveys.data?.endpoints || [];
  const projectCount = endpoints.reduce(
    (sum, ep) => sum + (ep.projects?.length || 0),
    0
  );

  const associatedSiteIds = new Set<string>();
  for (const site of siteList || []) {
    if (
      (site.siteOwnerships || []).some((so) => so.orgID === organisation.id)
    ) {
      associatedSiteIds.add(site.id);
    }
  }

  const associated: SiteOption[] = (siteList || [])
    .filter((s) => associatedSiteIds.has(s.id))
    .map((s) => ({ id: s.id, name: s.name, isAssociated: true }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const notAssociated: SiteOption[] = (siteList || [])
    .filter((s) => !associatedSiteIds.has(s.id))
    .map((s) => ({
      id: s.id,
      name: `${s.name} (not associated)`,
      isAssociated: false,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const siteOptions: SiteOption[] = [...associated, ...notAssociated];

  const customerDomain = getCustomerAppDomain();
  const apiHostname = getApiHostname();
  const getSurveyUrl = (ep: SurveyEndpointRecord) =>
    `${customerDomain}/survey/${ep.name}-${ep.shortcode}`;

  return (
    <Card body className="my-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong>Projects ({projectCount})</strong>
      </div>

      {fetchOrgSurveys.isLoading && <div>Loading...</div>}

      {fetchOrgSurveys.isError && (
        <div className="text-danger mb-2">
          Failed to load surveys.{" "}
          <Button
            variant="link"
            size="sm"
            className="p-0 text-danger"
            onClick={() => fetchOrgSurveys.refetch()}
          >
            Retry
          </Button>
        </div>
      )}

      {!fetchOrgSurveys.isLoading && endpoints.length === 0 && (
        <div className="text-muted small">
          No survey endpoints linked to this EPC.
        </div>
      )}

      {endpoints.length > 0 && (
        <div className="d-flex flex-column gap-3">
          {endpoints.map((ep) => (
            <div
              key={ep.id}
              className="border rounded p-3"
              style={{ background: "#fafafa" }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2 gap-3 flex-wrap">
                <div>
                  <div className="fw-bold">{ep.name}</div>
                  <div className="small">
                    Created {new Date(ep.created_at).toLocaleDateString()}
                  </div>
                  <div className="small">
                    <a
                      href={getSurveyUrl(ep)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {getSurveyUrl(ep)}
                    </a>
                  </div>
                </div>
                <SurveyEndpointLanguageSelector
                  endpointId={ep.id}
                  currentLanguage={ep.default_language ?? null}
                  label="Survey language"
                  minWidth={160}
                />
              </div>
              <ProjectsList
                endpoint={ep}
                siteOptions={siteOptions}
                sitesLoading={siteListQuery.isLoading}
                surveyUrl={getSurveyUrl(ep)}
                apiHostname={apiHostname}
              />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default OrgSurveys;
